import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Building2, Palette, Home, Mail, Heart, Sparkles } from "lucide-react";
import { IMAGES } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";
import PayPalDonate from "../components/site/PayPalDonate";
import RecentSupporters from "../components/site/RecentSupporters";

const DONATION_EMAIL = "isholaatolagbe@asanightworldwide.com";

const PROJECTS = [
  { icon: GraduationCap, title: "Educational Outreach", text: "Bringing Yorùbá language and heritage learning to schools, families, and communities." },
  { icon: Building2, title: "Office Development", text: "Establishing offices in Nigeria and major cities of the world to grow our reach." },
  { icon: Palette, title: "Music, Arts & Sculpture", text: "Developing programmes and exhibitions that keep Yorùbá creativity thriving." },
  { icon: Home, title: "Language Learning Centres", text: "Bequeath a property in your name for a Yorùbá Language Learning Centre in cities worldwide." },
];

export default function Support() {
  useSEO(
    "Support Us Today",
    "Support ÀsÀNigHT Worldwide — a non-profit saving the Yorùbá language from extinction. Donate one-time or recurring to fund educational outreach, arts, and language learning centres."
  );

  const scrollToForm = () => document.getElementById("donate-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[58vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={IMAGES.maternityStatue} alt="Support Yorùbá heritage" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Support Us Today</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero max-w-3xl"
          >
            Donors & Supporters
          </motion.h1>
        </div>
      </header>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <Reveal>
          <p className="text-2xl md:text-3xl font-serif text-forest leading-relaxed border-l-2 border-gold pl-6 mb-10">
            As a non-profit and non-governmental organization, ÀsÀNigHT Worldwide relies on the
            contributions of individuals, companies, foundations, international agencies, and
            governments to save the Yorùbá language from extinction.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-lg text-ink-muted leading-relaxed">
            We earnestly look forward to funding from programme-related public and private grants,
            sponsorships, and bequests. Every contribution helps us build a growing, open, honest,
            and transparent cultural heritage organization.
          </p>
        </Reveal>
      </section>

      {/* SUSTAINABILITY PROJECTS */}
      <section className="bg-sand border-y border-parchment">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">Where Your Support Goes</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mb-14">Support Our Sustainability Projects</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2">
            {PROJECTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07}>
                <div
                  data-testid={`support-project-${p.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="h-full border border-parchment rounded-xl p-8 bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300 flex gap-5"
                >
                  <span className="grid place-items-center w-12 h-12 rounded-full bg-forest text-cream shrink-0">
                    <p.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-ink mb-2">{p.title}</h3>
                    <p className="text-ink-muted leading-relaxed">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="text-center mt-14">
              <button
                onClick={scrollToForm}
                data-testid="support-cta-button"
                className="inline-flex items-center gap-2 bg-gold text-night font-semibold px-8 py-4 rounded-full hover:bg-gold-light transition-colors"
              >
                <Heart className="w-4 h-4" /> Support Us Today
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DONATE FORM + CONTACT */}
      <section id="donate-form" className="scroll-mt-24 max-w-5xl mx-auto px-6 py-24 grid lg:grid-cols-5 gap-10">
        <Reveal className="lg:col-span-3">
          <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">Give Securely</p>
          <h2 className="font-serif text-4xl text-ink mb-3">Make a Donation</h2>
          <p className="text-ink-muted mb-8">
            Support ÀsÀNigHT Worldwide with a secure one-time or monthly donation via PayPal. Choose
            any amount — every contribution helps save the Yorùbá language and heritage.
          </p>
          <PayPalDonate projects={PROJECTS} />
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="bg-forest text-cream rounded-2xl p-8 h-full grain-overlay">
            <Sparkles className="w-7 h-7 text-gold mb-5" />
            <h3 className="font-serif text-3xl mb-4">Set Up a Donation</h3>
            <p className="text-cream/75 leading-relaxed mb-6">
              For information on setting up donations, sponsorships, or bequests, please reach out
              to us directly.
            </p>
            <a
              href={`mailto:${DONATION_EMAIL}`}
              data-testid="donation-email-link"
              className="flex items-center gap-3 bg-cream/5 border border-cream/20 rounded-lg px-4 py-4 hover:border-gold transition-colors break-all"
            >
              <Mail className="w-5 h-5 text-gold shrink-0" />
              <span className="text-sm">{DONATION_EMAIL}</span>
            </a>
            <a
              href="mailto:asanight.worldwide@gmail.com"
              data-testid="donation-email-link-2"
              className="flex items-center gap-3 bg-cream/5 border border-cream/20 rounded-lg px-4 py-4 mt-3 hover:border-gold transition-colors break-all"
            >
              <Mail className="w-5 h-5 text-gold shrink-0" />
              <span className="text-sm">asanight.worldwide@gmail.com</span>
            </a>
            <p className="text-cream/60 text-sm mt-6 leading-relaxed">
              Every contribution — however large or small — helps make our dream of a growing,
              open, honest, and transparent cultural heritage organization a reality.
            </p>
          </div>
        </Reveal>
      </section>

      {/* RECENT SUPPORTERS WALL */}
      <RecentSupporters />
    </div>
  );
}
